import React, { useEffect, useState } from 'react';
import { components } from '../../api/api-abcscale';

export type SkuDto = components['schemas']['SkuDto'];
export type CalculatedPrice = components['schemas']['CalculatedPrice'];
export type SkuDiscountDto = components['schemas']['SkuDiscountDto'];
export type SkuPartsDiscountDto = components['schemas']['SkuPartsDiscountDto'];
export type SkuCategoryDto = components['schemas']['SkuCategoryDto'];
export type SearchIndexEntry = components['schemas']['SkuSearchIndexEntry'];

export type CountryCode = { code: string; name: string };
export type SkuSourceItem = { SourceId: string; Name: string };
export type SkuBrandItem = { Id: string; Name: string };
export type IndexState = 'idle' | 'loading' | 'ready' | 'error';

export const MIN_CHARS = 3;

export function useCountryCodes(): CountryCode[] {
  const [countries, setCountries] = useState<CountryCode[]>([]);
  useEffect(() => {
    fetch('/data/sku/country-codes.json')
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((data: CountryCode[]) => setCountries(data))
      .catch(() => {});
  }, []);
  return countries;
}

export function useSources(): SkuSourceItem[] {
  const [sources, setSources] = useState<SkuSourceItem[]>([]);
  useEffect(() => {
    fetch('/data/sku/sources.json')
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((data: SkuSourceItem[]) => setSources(data))
      .catch(() => {});
  }, []);
  return sources;
}

export function useCategories(): SkuCategoryDto[] {
  const [categories, setCategories] = useState<SkuCategoryDto[]>([]);
  useEffect(() => {
    fetch('/data/sku/categories.json')
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((data: any[]) => {
        const normalized: SkuCategoryDto[] = data.map(d => ({
          categoryId: d.categoryId ?? d.CategoryId,
          categoryName: d.categoryName ?? d.CategoryName,
          type: d.type ?? d.Type ?? null,
          description: d.description ?? d.Description ?? null,
        }));
        setCategories(normalized);
      })
      .catch(() => {});
  }, []);
  return categories;
}

export function useBrands(): SkuBrandItem[] {
  const [brands, setBrands] = useState<SkuBrandItem[]>([]);
  useEffect(() => {
    fetch('/data/sku/brands.json')
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((data: SkuBrandItem[]) => setBrands(data))
      .catch(() => {});
  }, []);
  return brands;
}

export const fmtDate = (val: string | null | undefined) =>
  val == null ? '—' : new Date(val).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });

export const fmtCurrency = (val: number | string | null | undefined) =>
  val == null ? '—' : Number(val).toLocaleString('es-MX', { style: 'currency', currency: 'USD' });

export const fmtPercent = (val: number | string | null | undefined) =>
  val == null ? '—' : `${(Number(val) * 100).toFixed(2)}%`;

export const scoreClass = (score: number | string | null | undefined) => {
  const n = Number(score);
  if (isNaN(n)) return '';
  if (n >= 1)    return 'pricelist-badge--green';
  if (n >= 0.85) return 'pricelist-badge--green';
  if (n >= 0.60) return 'pricelist-badge--orange';
  return 'pricelist-badge--red';
};

export const pctToInput = (val: number | string | null | undefined): string => {
  if (val == null) return '';
  return String(+(Number(val) * 100).toFixed(4));
};

export const fmtCostField = (val: number | string | null | undefined, type: number | string | null | undefined) => {
  if (val == null) return '—';
  return Number(type) === 2 ? fmtCurrency(val) : fmtPercent(val);
};

export const costToInput = (val: number | string | null | undefined, type: number | string | null | undefined): string => {
  if (val == null) return '';
  return Number(type) === 2 ? String(Number(val)) : pctToInput(val);
};

export const COST_TYPE_LABELS: Record<string, string> = {
  '0': '% precio lista',
  '1': '% precio c/dto.',
  '2': 'Monto fijo',
};

export function applyHighlight(text: string, indices: ReadonlyArray<readonly [number, number]> | undefined): React.ReactNode {
  if (!indices || indices.length === 0) return text;
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  for (const [start, end] of indices) {
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(<mark key={start} className="pricelist-hl">{text.slice(start, end + 1)}</mark>);
    cursor = end + 1;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

export function groupDiscountsByCategory(discounts: SkuDiscountDto[]): Map<string, SkuDiscountDto[]> {
  const map = new Map<string, SkuDiscountDto[]>();
  for (const d of discounts) {
    const key = d.category ?? '(sin categoría)';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(d);
  }
  return map;
}

export const DiscountTreeGroup: React.FC<{
  category: string;
  items: SkuDiscountDto[];
  selectedId: string;
  onSelect: (id: string) => void;
  defaultOpen: boolean;
}> = ({ category, items, selectedId, onSelect, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="pricelist-tree-group">
      <button className="pricelist-tree-category" onClick={() => setOpen(o => !o)}>
        <span className="pricelist-tree-chevron">{open ? '▾' : '▸'}</span>
        {category}
      </button>
      {open && (
        <div className="pricelist-tree-items">
          {items.map(item => (
            <button
              key={item.discountId ?? item.subcategory}
              className={`pricelist-tree-item${selectedId === item.discountId ? ' pricelist-tree-item--selected' : ''}`}
              onClick={() => onSelect(selectedId === item.discountId ? '' : (item.discountId ?? ''))}
            >
              <span className="pricelist-tree-item-sub">{item.subcategory ?? '(sin subcategoría)'}</span>
              <span className="pricelist-tree-item-rate">{fmtPercent(item.discountRate)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
